import availabilitiesAndConstraintsService from 'src/services/inventory/sales-restrictions/AvailabilitiesAndConstraintsService'
import { getInventoryStrategy } from 'src/services/inventory/sales-restrictions/helpers'

class AvailableStockService {
  static #instance = null

  static #availabilitiesAndConstraintsService = null

  constructor ({
    availabilitiesAndConstraintsService
  }) {
    if (!AvailableStockService.#instance) {
      AvailableStockService.#availabilitiesAndConstraintsService = availabilitiesAndConstraintsService

      AvailableStockService.#instance = this
    }
    return AvailableStockService.#instance
  }

  availableStock (model, options) {
    const inventoryStrategy = getInventoryStrategy()

    if (_.isNil(inventoryStrategy)) return null

    if (!AvailableStockService.#availabilitiesAndConstraintsService.isModelInventoriable(model)) return null

    const {
      item,
      itemOrderItem,
      order,
      orderItem,
      selectedItemVariationId: selectedItemVariationIdFromOptions,
      selectedModifierIds: selectedModifierIdsFromOptions
    } = options

    const selectedItemVariationId = selectedItemVariationIdFromOptions || itemOrderItem?.itemVariation?.id
    const selectedModifierIds = selectedModifierIdsFromOptions || _.map(itemOrderItem?.itemModifiers?.data, 'modifier.id')
    const screen = order?.nearestShowing?.screen
    const deliveryType = AvailableStockService.#deliveryType({ orderItem, order })

    switch (model.typeName) {
      case 'ItemVariation':
        return AvailableStockService.#availableStockForItemVariation(model, {
          deliveryType,
          screen,
          selectedModifierIds
        })
      case 'Modifier':
        return AvailableStockService.#availableStockForModifier(model, {
          deliveryType,
          item,
          screen,
          selectedItemVariationId
        })
      case 'Item':
        return AvailableStockService.#availableStockForItem(model, {
          deliveryType,
          screen
        })
      case 'RegisterButton': {
        switch (model.type) {
          case 'item':
            return AvailableStockService.#availableStockForItem(model.item, {
              deliveryType,
              screen
            })
          case 'item-variation':
            return AvailableStockService.#availableStockForItemVariation(model.itemVariation, {
              deliveryType,
              screen
            })
          default:
            return null
        }
      }
      default:
        console.warn(`Unsupported model type: ${model.typeName}`)
        return null
    }
  }

  static #deliveryType ({
    orderItem,
    order
  }) {
    const deliveryTypeFromOrderItem = orderItem?.deliveryTypeName
    const deliveryTypeFromOrder = (orderItem?.order || order)?.deliveryTypeName
    return deliveryTypeFromOrderItem || deliveryTypeFromOrder
  }

  static #availableStockForItemVariation (itemVariation, {
    deliveryType,
    screen,
    selectedModifierIds
  }) {
    const availabilitiesAndConstraintsByInventoryRecipeIds = AvailableStockService.#availabilitiesAndConstraintsService.getAvailabilitiesAndConstraintsForModel(itemVariation, {
      deliveryType,
      item: itemVariation.item,
      screen
    })

    if (_.isEmpty(availabilitiesAndConstraintsByInventoryRecipeIds)) return null

    return _(availabilitiesAndConstraintsByInventoryRecipeIds)
      .entries()
      .map(([inventoryRecipeId, availabilityAndConstraintsObject]) => {
        return _.includes(itemVariation.invalidInventoryRecipeIds(selectedModifierIds), +inventoryRecipeId)
          ? null
          : `${availabilityAndConstraintsObject.available}`
      })
      .compact()
      .max()
  }

  static #availableStockForModifier (modifier, {
    deliveryType,
    item,
    screen,
    selectedItemVariationId
  }) {
    const availabilitiesAndConstraintsByInventoryRecipeIds = AvailableStockService.#availabilitiesAndConstraintsService.getAvailabilitiesAndConstraintsForModel(modifier, {
      deliveryType,
      item,
      screen
    })

    if (_.isEmpty(availabilitiesAndConstraintsByInventoryRecipeIds)) return null

    return _(availabilitiesAndConstraintsByInventoryRecipeIds)
      .entries()
      .map(([inventoryRecipeId, availabilityAndConstraintsObject]) => {
        return _.includes(modifier.invalidInventoryRecipeIds(selectedItemVariationId), +inventoryRecipeId)
          ? null
          : `${availabilityAndConstraintsObject.available}`
      })
      .compact()
      .max()
  }

  static #availableStockForItem (item, {
    deliveryType,
    screen
  }) {
    const availableStock = _(item.activeItemVariations).filter('hasRecipe').map(activeItemVariation => {
      const itemVariationAvailableStock = AvailableStockService.#availableStockForItemVariation(activeItemVariation, {
        deliveryType,
        screen
      })

      const modifiersAvailableStock = _(item.itemModifierSets.data)
        .map(itemModifierSet => {
          /**
           * If there's no minimum requirement, we can't be constrained by
           * modifier availability.
           */
          if (!itemModifierSet.minSelectionCount) return null

          const activeModifiers = itemModifierSet.modifierSet.activeModifiers
          const numActiveModifiersWithoutRecipes = _.filter(activeModifiers, { hasRecipe: false }).length

          if (numActiveModifiersWithoutRecipes > itemModifierSet.minSelectionCount) {
            /**
             * If there are enough active modifiers without recipes to fulfill
             * the minimum requirement, we can ignore the remaining modifier
             * availability.
             */
            return null
          }

          const numActiveModifiersWithRecipesToCheckAvailabilityFor = itemModifierSet.minSelectionCount - numActiveModifiersWithoutRecipes

          /**
           * We want the lowest of the top N modifier availabilities, where N
           * represents the remaining minimum requirments to fulfill.
           */
          return _(activeModifiers)
            .filter('hasRecipe')
            .map(activeModifier => {
              return AvailableStockService.#availableStockForModifier(activeModifier, {
                deliveryType,
                item,
                screen,
                selectedItemVariationId: activeItemVariation.id
              })
            })
            .sortBy()
            .reverse()
            .take(numActiveModifiersWithRecipesToCheckAvailabilityFor)
            .min()
        })
        .compact()
        .value()

      /**
       * The item variation availability is determined by its own availability,
       * or the availability of any of the requiredd item modifiers, whichever
       * is lowest.
       */
      return _.min([itemVariationAvailableStock, ...modifiersAvailableStock])
    }).max()

    return typeof availableStock === 'string' ? availableStock : null
  }
}

const availableStockService = new AvailableStockService({
  availabilitiesAndConstraintsService
})

export default availableStockService
